import { useEffect, useState } from 'react';
import { Card, EmptyState, Input } from '@adop/ui';
import { ScrollText, Search } from 'lucide-react';
import { fetchAdminAudit } from '../../lib/admin-api';
import { useAsync } from '../../lib/useAsync';
import { useDebouncedValue } from '../../lib/use-debounced-value';
import { isUuid } from '../../lib/admin-table';
import { formatLocalDateTime } from '@adop/shared';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { LoadingBlock } from '../../components/LoadingState';
import { AdminPagination } from './AdminPagination';

const PAGE_SIZE = 25;

export function AdminAuditPage() {
  const [restaurantId, setRestaurantId] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const debouncedRestaurant = useDebouncedValue(restaurantId, 400);
  const debouncedAction = useDebouncedValue(action, 400);

  const trimmedRestaurant = debouncedRestaurant.trim();
  const restaurantFilter = isUuid(trimmedRestaurant) ? trimmedRestaurant : undefined;
  const restaurantInvalid = trimmedRestaurant.length > 0 && restaurantFilter === undefined;

  useEffect(() => {
    setPage(1);
  }, [restaurantFilter, debouncedAction]);

  const state = useAsync(
    () =>
      fetchAdminAudit({
        page,
        page_size: PAGE_SIZE,
        restaurant_id: restaurantFilter,
        action: debouncedAction.trim() || undefined,
      }),
    [page, restaurantFilter, debouncedAction],
  );

  const entries = state.data?.entries ?? [];
  const total = state.data?.total ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Audit"
        description="Every admin mutation and support view, newest first."
      />

      {state.error ? <Alert>{state.error}</Alert> : null}

      <Card>
        <div className="grid gap-3 border-b border-zinc-200 p-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-500" htmlFor="audit-restaurant">
              Restaurant id
            </label>
            <Input
              id="audit-restaurant"
              value={restaurantId}
              onChange={(event) => setRestaurantId(event.target.value)}
              placeholder="Restaurant UUID"
              error={restaurantInvalid ? 'Enter a valid UUID' : undefined}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-500" htmlFor="audit-action">
              Action
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <Input
                id="audit-action"
                value={action}
                onChange={(event) => setAction(event.target.value)}
                placeholder="e.g. admin.support_view"
                className="pl-9"
              />
            </div>
          </div>
        </div>

        {state.loading && !state.data ? (
          <LoadingBlock />
        ) : entries.length === 0 ? (
          <EmptyState
            icon={<ScrollText className="h-6 w-6" />}
            title="No audit entries"
            description="Admin actions will appear here."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[64rem] text-left text-sm">
                <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Time (UTC)</th>
                    <th className="px-4 py-2 font-medium">Restaurant</th>
                    <th className="px-4 py-2 font-medium">Actor</th>
                    <th className="px-4 py-2 font-medium">Action</th>
                    <th className="px-4 py-2 font-medium">Target</th>
                    <th className="px-4 py-2 font-medium">Meta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="align-top hover:bg-zinc-50">
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-zinc-500">
                        {formatLocalDateTime(entry.created_at, 'UTC')}
                      </td>
                      <td className="px-4 py-3 text-zinc-700">{entry.restaurant_name}</td>
                      <td className="px-4 py-3 text-zinc-600">
                        {entry.actor_email ?? (entry.actor_user_id ? entry.actor_user_id.slice(0, 8) : 'System')}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-zinc-900">{entry.action}</span>
                      </td>
                      <td className="max-w-[16rem] truncate px-4 py-3 text-zinc-600" title={entry.target ?? ''}>
                        {entry.target ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        {entry.meta ? (
                          <details>
                            <summary className="cursor-pointer text-xs text-zinc-500 hover:text-zinc-900">
                              {JSON.stringify(entry.meta).slice(0, 60)}
                              {JSON.stringify(entry.meta).length > 60 ? '…' : ''}
                            </summary>
                            <pre className="mt-1 max-w-[24rem] overflow-x-auto rounded bg-zinc-50 p-2 text-xs text-zinc-700">
                              {JSON.stringify(entry.meta, null, 2)}
                            </pre>
                          </details>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              unit="entries"
            />
          </>
        )}
      </Card>
    </div>
  );
}
